import { Box, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

/**
 * Single source of truth for task status presentation. Colours match the task
 * colours used across the rest of the app (all-tasks page, grids, chips).
 *
 * Each entry exposes:
 *   - label   : canonical full label
 *   - short   : compact label used inside the dense table badge
 *   - solid   : chip / color-bar fill
 *   - onSolid : contrast text colour rendered on top of `solid`
 */
export const STATUS_META = {
  'to start': {
    label: 'To Start',
    short: 'To Start',
    solid: '#6B7280',
    onSolid: '#FFFFFF',
  },
  doing: {
    label: 'Doing',
    short: 'Doing',
    solid: '#FCD34D',
    onSolid: '#440055',
  },
  'on hold': {
    label: 'On hold',
    short: 'On Hold',
    solid: '#EF4444',
    onSolid: '#FFFFFF',
  },
  'task complete': {
    label: 'Task Complete',
    short: 'Complete',
    solid: '#10B981',
    onSolid: '#FFFFFF',
  },
  documented: {
    label: 'Documented',
    short: 'Documented',
    solid: '#8B5CF6',
    onSolid: '#FFFFFF',
  },
  'closed w review': {
    label: 'Closed w Review',
    short: 'Reviewed',
    solid: '#3B82F6',
    onSolid: '#FFFFFF',
  },
  default: {
    label: 'Unknown',
    short: 'Unknown',
    solid: '#9CA3AF',
    onSolid: '#FFFFFF',
  },
};

/**
 * Resolves the metadata for a given status, falling back to a neutral default.
 * @param {string} status - Status value (case-insensitive)
 * @returns {{label: string, short: string, solid: string, soft: string, text: string}}
 */
export const getStatusMeta = (status) =>
  STATUS_META[(status || '').toLowerCase()] || STATUS_META.default;

/**
 * Back-compat colour accessor used across the app.
 * @param {string} status - Status value (case-insensitive)
 * @returns {{bg: string, bgHover: string, text: string}}
 */
export const getStatusColor = (status) => {
  const meta = getStatusMeta(status);
  return { bg: meta.solid, bgHover: meta.solid, text: meta.onSolid };
};

const StyledChip = styled(Chip)(({ status }) => {
  const meta = getStatusMeta(status);
  return {
    minWidth: '90px',
    '&.MuiChip-root': {
      backgroundColor: meta.solid,
      color: meta.onSolid,
      '&:hover': {
        backgroundColor: meta.solid,
      },
    },
  };
});

const StatusChip = ({ status, ...props }) => {
  return (
    <StyledChip
      label={status}
      status={(status || '').toLowerCase()}
      size="small"
      {...props}
    />
  );
};

/**
 * Compact status badge: solid status colour with contrast text. Reads as a
 * small tag rather than a full-width button.
 */
export const StatusBadge = ({ status, sx }) => {
  const meta = getStatusMeta(status);
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: 1,
        backgroundColor: meta.solid,
        color: meta.onSolid,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {meta.short}
    </Box>
  );
};

/**
 * Horizontal legend mapping each status to its colour. Renders short labels with
 * a tooltip showing the full label for context.
 */
export const StatusLegend = ({ sx }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, ...sx }}>
    {Object.entries(STATUS_META)
      .filter(([key]) => key !== 'default')
      .map(([key, meta]) => (
        <Tooltip key={key} title={meta.label} arrow>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="span"
              sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: meta.solid }}
            />
            <Box component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
              {meta.short}
            </Box>
          </Box>
        </Tooltip>
      ))}
  </Box>
);

export default StatusChip;
